import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import SyncAltIcon from "@material-ui/icons/SyncAlt";
import { Button } from "antd";
import { COMPATIBLE_COLORMAP_NAMES } from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import styled from "styled-components/macro";
import { buildColormapPalette } from "utils/color/buildColormapPalette";

import { spaceOutColorsForDisplay } from "../util/spaceOutColorsForDisplay";

// Every generated colormap palette, built once at module load — the generator
// is pure and COMPATIBLE_COLORMAP_NAMES is a static list, so this never needs
// to be recomputed per render or per org. Union mode (below) always offers
// every one of these except where an org palette wins a name collision (see
// isPlasmaName).
const ALL_GENERATED_COMPATIBLE_PALETTES = COMPATIBLE_COLORMAP_NAMES.map((name) =>
  buildColormapPalette(name)
).filter(Boolean);

// Plasma is the one generated name whose id genuinely matters downstream —
// useRigFracSeriesColors' default-palette discriminator compares against
// RIG_FRAC_PLASMA_PALETTE.id (generated-plasma). Every other name collision in
// union mode keeps the org's own palette winning (the pre-union behaviour,
// unchanged) so that, for example, an org's 4-stop "Jet" doesn't get silently
// replaced by the generated 12-shade one on a fresh pick.
const isPlasmaName = (name) => name.toLowerCase() === "plasma";

const ColorPaletteList = ({
  selected,
  onSelect,
  onReverse = null,
  useCustomPalettes = true,
  // Third mode, independent of useCustomPalettes: org palettes UNIONED with
  // every generated colormap palette, rather than either list on its own.
  // Nothing the org already has stops being selectable — the only palette
  // ever dropped from the org side is one named "Plasma" (see isPlasmaName),
  // because the generated Plasma entry must be the only one selectable here.
  includeGeneratedPalettes = false
}) => {
  const [colorSchemes, setColorSchemes] = useState([]);

  const palettes = useSelector((state: RootState) => state.userSetting.colorPalettes);

  const reverseColors = (names = null, event = null) => {
    const namesToReverse = names || colorSchemes.map((c) => c.name);
    const isSingleReverse = names?.length === 1;
    const newSchemes = colorSchemes.map((c) => {
      const haveToReverse = namesToReverse.includes(c.name);
      return {
        ...c,
        reverse: haveToReverse ? !c.reverse : c.reverse
      };
    });
    setColorSchemes(newSchemes);

    // Auto-select the palette whose reverse button was clicked
    if (isSingleReverse) {
      const updatedSelected = newSchemes.find((c) => c.name === namesToReverse[0]);
      if (updatedSelected) {
        if (onReverse) {
          onReverse(updatedSelected);
        } else {
          onSelect(updatedSelected);
        }
      }
    }

    event?.stopPropagation();
  };

  // Generate palette objects for all compatible colormap names that don't
  // exist in Redux. Only used by the `!useCustomPalettes` mode below — union
  // mode has its own name-collision rule (isPlasmaName), so this is skipped
  // entirely rather than computed and left unread.
  const generatedCompatiblePalettes = useMemo(() => {
    if (includeGeneratedPalettes || !palettes) return [];
    const existingNames = new Set(palettes.map((p) => p.name.toLowerCase()));
    return COMPATIBLE_COLORMAP_NAMES.filter((name) => !existingNames.has(name))
      .map((name) => buildColormapPalette(name))
      .filter(Boolean);
  }, [palettes, includeGeneratedPalettes]);

  useEffect(() => {
    const safePalettes = palettes ?? [];
    let filteredPalettes = safePalettes;
    if (includeGeneratedPalettes) {
      // Union: every org palette, MINUS one named "Plasma" (that name's org
      // entry never survives — see isPlasmaName), plus every generated
      // compatible palette that doesn't collide with a name the org still
      // has after that removal. A non-Plasma collision (e.g. org "Jet" vs.
      // generated "Jet") drops the GENERATED duplicate, keeping the org's own
      // palette exactly as selectable as it was before this mode existed.
      const orgPalettesForUnion = safePalettes.filter(
        (palette) => !isPlasmaName(palette.name)
      );
      const orgNamesForUnion = new Set(
        orgPalettesForUnion.map((palette) => palette.name.toLowerCase())
      );
      const generatedPalettesForUnion = ALL_GENERATED_COMPATIBLE_PALETTES.filter(
        (palette) =>
          isPlasmaName(palette.name) || !orgNamesForUnion.has(palette.name.toLowerCase())
      );
      filteredPalettes = [...orgPalettesForUnion, ...generatedPalettesForUnion];
    } else if (!useCustomPalettes) {
      const reduxCompatible = safePalettes.filter((palette) =>
        COMPATIBLE_COLORMAP_NAMES.includes(palette.name.toLowerCase())
      );
      filteredPalettes = [...reduxCompatible, ...generatedCompatiblePalettes];
    }
    if (filteredPalettes) {
      const scheme = [
        //make copy of the colors
        ...filteredPalettes.map((c) => {
          const clone = { ...c };
          if (c.name.toLowerCase() === selected?.name?.toLowerCase()) {
            clone.reverse = selected?.reverse ?? false;
          }
          return clone;
        })
      ];
      setColorSchemes(scheme);
    }
  }, [
    palettes,
    selected?.name,
    selected?.reverse,
    useCustomPalettes,
    includeGeneratedPalettes,
    generatedCompatiblePalettes
  ]);

  return (
    <Wrapper>
      <h5>Select Colour Palette</h5>
      <StyledButton onClick={() => reverseColors()}>
        <SyncAltIcon /> Reverse all palettes
      </StyledButton>

      {colorSchemes.map((scheme) => {
        const { name, colors, reverse } = scheme;
        const displayedColors = reverse ? [...colors].reverse() : colors;
        const spacedOutColors = spaceOutColorsForDisplay(displayedColors);
        return (
          <Item
            data-testid="color-palette-item"
            className={`color-palette-item-${name}`}
            key={name}
            selected={selected && name === selected.name}
            onClick={() => {
              onSelect(scheme);
            }}>
            <div className="color-scheme-descriptor">{name}</div>
            <Palette>
              {spacedOutColors.map((color, i) => (
                <Swatch key={i} color={color} />
              ))}

              <SyncButton
                type="link"
                icon={<SyncAltIcon fontSize="large" />}
                onClick={(event) => reverseColors([name], event)}
              />
            </Palette>
          </Item>
        );
      })}
    </Wrapper>
  );
};

export default ColorPaletteList;

const Wrapper = styled.div`
  display: grid;
  padding-bottom: var(--space-4);
  color: var(--color-text-60);
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  gap: 8px;
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0 16px 8px 16px;
`;

const Item = styled.div`
  display: grid;
  color: ${(p) => (p.selected ? "var(--color-text)" : "inherit")};
  background-color: ${(p) => (p.selected ? "var(--color-text-06)" : "transparent")};
  font-weight: var(--fontWeightMedium);
  padding: var(--space-1) var(--space-4);
  user-select: none;
  cursor: pointer;
  border: 1px solid var(--smoke);
  border-radius: var(--border-radius);

  &:hover {
    color: var(--color-text);
    background-color: var(--color-text-06);
  }
`;

const Palette = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const Swatch = styled.div`
  width: 18px;
  height: 18px;
  background-color: ${(p) => p.color};
  border-radius: 2px;
`;

const SyncButton = styled(Button)`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: currentColor;
  margin-left: var(--space-2);
`;
